import { apiClient } from './client';
import type { Role } from './users';
import { setAccessToken } from './client';
import type { TokenResponse } from './auth';

export interface OtherTenantSummary {
  tenant_id: string;
  tenant_slug: string;
  tenant_name: string;
}

export interface Invitation {
  id: string;
  email: string;
  role: Role;
  // NULL = platform-level invitation (10NEWTONS superadmin onboarding,
  // sent via POST /api/v1/admin/invitations). Tenant-scoped invitations
  // always carry a real tenant_id + tenant_name.
  tenant_id: string | null;
  tenant_name: string | null;
  // True for a read-only tenant_admin invite — the accepted membership
  // will be write-locked. Optional so older fixtures default to false.
  read_only?: boolean;
  expires_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
  created_at: string;
  // ROADMAP #36: populated when the invited email is already an
  // active participant in one or more OTHER tenants. Empty when
  // the email is a first-time participant. Informational —
  // the invitation still ships either way; the portal renders a
  // non-blocking warning. Optional in the type so older fixtures
  // default to "no warning" rather than failing.
  enrolled_elsewhere?: OtherTenantSummary[];
}

export interface InvitationPreview {
  email: string;
  /** UUID of the target tenant, or null for a platform-level invite. */
  tenant_id: string | null;
  tenant_name: string | null;
  role: Role;
  expires_at: string;
  user_exists: boolean;
  /** LEGAL-TERMS: the account already accepted the current versions. */
  legal_accepted?: boolean;
}

export async function listInvitations(
  tenantId: string,
  includeSettled = false,
): Promise<Invitation[]> {
  const { data } = await apiClient.get<Invitation[]>(
    `/api/v1/tenants/${tenantId}/invitations`,
    { params: { include_settled: includeSettled } },
  );
  return data;
}

export async function createInvitation(
  tenantId: string,
  body: { email: string; role: Role; read_only?: boolean },
): Promise<Invitation> {
  const { data } = await apiClient.post<Invitation>(
    `/api/v1/tenants/${tenantId}/invitations`,
    body,
  );
  return data;
}

export async function revokeInvitation(
  tenantId: string,
  invitationId: string,
): Promise<void> {
  await apiClient.delete(
    `/api/v1/tenants/${tenantId}/invitations/${invitationId}`,
  );
}

export interface BulkInviteRowResult {
  email: string;
  /** "sent" | "invalid_format" | "already_member" | "duplicate_in_list" | "license_limit" */
  status: string;
  detail: string | null;
  invitation_id: string | null;
  // ROADMAP #36: populated when the invited email already holds
  // an active participant membership in one or more OTHER tenants.
  // Surfaced as a non-blocking warning chip; the invitation still
  // ships (status stays "sent"). Optional in the type so older
  // fixtures + (theoretically) older API responses default to "no
  // warning" rather than crashing the render.
  enrolled_elsewhere?: OtherTenantSummary[];
}

export interface BulkInviteResponse {
  sent: number;
  skipped: number;
  results: BulkInviteRowResult[];
}

export async function bulkInviteParticipants(
  tenantId: string,
  emails: string[],
): Promise<BulkInviteResponse> {
  const { data } = await apiClient.post<BulkInviteResponse>(
    `/api/v1/tenants/${tenantId}/invitations/bulk`,
    { emails },
  );
  return data;
}

export async function previewInvitation(token: string): Promise<InvitationPreview> {
  const { data } = await apiClient.get<InvitationPreview>(
    '/api/v1/invitations/preview',
    { params: { token } },
  );
  return data;
}

export async function acceptInvitation(body: {
  token: string;
  password?: string;
  full_name?: string;
  legal_document_ids?: string[];
}): Promise<TokenResponse> {
  const { data } = await apiClient.post<TokenResponse>(
    '/api/v1/invitations/accept',
    body,
  );
  setAccessToken(data.access_token);
  return data;
}
